import { SUPABASE_URL, SUPABASE_KEY } from './supabase';
import { getActiveOrganisationId } from './activeOrg';

function withActiveOrg(body) {
  const activeOrgId = getActiveOrganisationId();
  if (!activeOrgId || !body || body.organisation_id !== undefined) return body;
  return { ...body, organisation_id: activeOrgId };
}

/**
 * AI helpers — thin wrappers over Supabase Edge Functions that proxy Anthropic.
 * The functions keep the API key server-side. If a function isn't deployed yet,
 * callers should catch and degrade gracefully (the UI shows a friendly notice).
 */

export class AiUnavailableError extends Error {
  constructor(message) {
    super(message || 'AI service is not available yet.');
    this.name = 'AiUnavailableError';
  }
}

let getAuth0AccessTokenFn = null;

/**
 * Wire Auth0 access token (API audience) for AI edge function calls.
 * Set once from AuthContext alongside setAuth0IdTokenGetter.
 */
export function setAuth0AccessTokenGetter(fn) {
  getAuth0AccessTokenFn = fn || null;
}

async function getAuth0AccessToken() {
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
  if (!audience) {
    throw new AiUnavailableError('Missing REACT_APP_AUTH0_AUDIENCE');
  }
  if (!getAuth0AccessTokenFn) {
    throw new AiUnavailableError('Sign in required to use AI features.');
  }
  return getAuth0AccessTokenFn({ authorizationParams: { audience } });
}

/**
 * Invoke an Auth0-gated Edge Function (verify_jwt = false; token checked in function code).
 */
async function invokeEdgeFunction(functionName, body) {
  const token = await getAuth0AccessToken();
  let resp;
  try {
    resp = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(withActiveOrg(body)),
    });
  } catch (e) {
    throw new AiUnavailableError(e?.message || 'Network error calling AI service.');
  }

  let data;
  try {
    data = await resp.json();
  } catch {
    throw new AiUnavailableError(`Edge function ${functionName} returned invalid JSON (${resp.status}).`);
  }

  if (!resp.ok) {
    throw new AiUnavailableError(data?.error || `Edge function ${functionName} failed (${resp.status}).`);
  }
  if (data?.error) throw new AiUnavailableError(data.error);
  return data;
}

/**
 * Summarise a set of RAID items into an executive-ready briefing.
 * @param {{ programmeName?: string, items: Array }} payload
 * @returns {Promise<string>} markdown/plain-text summary
 */
export async function summariseRisks(payload) {
  const data = await invokeEdgeFunction('ai-summarise', payload);
  return data?.summary || '';
}

/**
 * Draft a weekly status report from live programme data.
 * @param {{ programmeName?: string, weekEnding?: string, data: object }} payload
 * @returns {Promise<object>} structured draft (see ai-status-draft function)
 */
export async function draftStatusReport(payload) {
  const data = await invokeEdgeFunction('ai-status-draft', payload);
  if (!data?.draft) throw new AiUnavailableError('No draft returned.');
  return data.draft;
}

/**
 * Conversational Rex — multi-turn chat with screen context.
 * @param {{ messages: Array<{role:string,content:string}>, screen?: string, context?: object }} payload
 * @returns {Promise<string>}
 */
export async function rexChat(payload) {
  const data = await invokeEdgeFunction('rex-chat', payload);
  return data?.reply || '';
}

/**
 * Stream Rex chat tokens (charter drafting). Calls rex-chat with stream: true.
 * @param {object} payload — same shape as rexChat
 * @param {{ onToken?: (chunk: string) => void }} handlers
 * @returns {Promise<string>} full assembled reply
 */
export async function rexChatStream(payload, { onToken } = {}) {
  const token = await getAuth0AccessToken();
  let resp;
  try {
    resp = await fetch(`${SUPABASE_URL}/functions/v1/rex-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(withActiveOrg({ ...payload, stream: true })),
    });
  } catch (e) {
    throw new AiUnavailableError(e?.message || 'Network error calling AI service.');
  }

  const contentType = resp.headers.get('content-type') || '';
  if (!resp.ok || !contentType.includes('text/event-stream') || !resp.body) {
    let data;
    try {
      data = await resp.json();
    } catch {
      throw new AiUnavailableError(`Edge function rex-chat failed (${resp.status}).`);
    }
    throw new AiUnavailableError(data?.error || `Edge function rex-chat failed (${resp.status}).`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      for (const line of part.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw || raw === '[DONE]') continue;
        try {
          const evt = JSON.parse(raw);
          if (evt.type === 'content_block_delta' && evt.delta?.type === 'text_delta' && evt.delta.text) {
            fullText += evt.delta.text;
            onToken?.(evt.delta.text);
          }
        } catch {
          // ignore malformed SSE chunks
        }
      }
    }
  }

  return fullText.trim() || 'I could not generate a response. Please try again.';
}

/**
 * Parse Rex's charter proposal into structured header/section JSON.
 * @param {{ proposal: string, header?: object, sections?: object }} payload
 * @returns {Promise<{ header: object|null, sections: object }>}
 */
export async function rexApplyCharter(payload) {
  const data = await invokeEdgeFunction('rex-apply-charter', payload);
  if (!data?.draft) throw new AiUnavailableError('No charter draft returned.');
  return data.draft;
}

/**
 * Parse Rex's taxonomy proposal into structured node JSON.
 * @param {{ proposal: string, existing_nodes?: Array }} payload
 * @returns {Promise<{ nodes: Array }>}
 */
export async function rexApplyTaxonomy(payload) {
  const data = await invokeEdgeFunction('rex-apply-taxonomy', payload);
  if (!data?.draft) throw new AiUnavailableError('No taxonomy draft returned.');
  return data.draft;
}
